import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BillingCalculation } from '@/lib/billingPlans';

export interface EstablishmentBillingData {
  establishment: {
    id: string;
    name: string;
    slug: string;
    planType: string;
    isActive: boolean;
    trialEndsAt: string | null;
    createdAt: string;
  };
  stats: {
    arrivedCount: number;
    arrivedPeople: number;
    totalReservations: number;
  };
  billing: BillingCalculation;
  record: {
    id: string;
    status: 'pending' | 'invoiced' | 'paid';
    invoice_number: string | null;
    invoice_sent_at: string | null;
    paid_at: string | null;
    notes: string | null;
  } | null;
}

export interface BillingSummary {
  totalEstablishments: number;
  activeEstablishments: number;
  totalArrivedReservations: number;
  totalArrivedPeople: number;
  totalAmount: number;
  pendingInvoices: number;
  invoicedCount: number;
  paidCount: number;
}

interface AdminBillingResponse {
  month: string;
  billingData: EstablishmentBillingData[];
  summary: BillingSummary;
}

export function useAdminBilling(month: string) {
  const [data, setData] = useState<EstablishmentBillingData[]>([]);
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBillingData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setError('Non authentifié');
        return;
      }

      const response = await supabase.functions.invoke<AdminBillingResponse>('get-admin-billing', {
        body: null,
        headers: {},
      });

      // Append month as query param by calling with custom fetch
      const { data: result, error: fetchError } = await supabase.functions.invoke<AdminBillingResponse>(
        `get-admin-billing?month=${month}`
      );

      if (fetchError) {
        console.error('Error fetching admin billing:', fetchError);
        setError(fetchError.message || 'Erreur lors de la récupération des données');
        return;
      }

      setData(result?.billingData || []);
      setSummary(result?.summary || null);
    } catch (err) {
      console.error('Error in useAdminBilling:', err);
      setError('Erreur lors de la récupération des données');
    } finally {
      setLoading(false);
    }
  }, [month]);

  const updateBillingRecord = useCallback(async (
    establishmentId: string,
    billing: BillingCalculation,
    updates: {
      status?: 'pending' | 'invoiced' | 'paid';
      invoiceNumber?: string;
      notes?: string;
    }
  ) => {
    try {
      const { error } = await supabase.functions.invoke('update-billing-record', {
        body: {
          establishmentId,
          month,
          planType: billing.planType,
          baseAmount: billing.baseAmount,
          reservationCount: billing.reservationCount,
          reservationAmount: billing.reservationAmount,
          totalAmount: billing.total,
          ...updates
        }
      });

      if (error) throw error;

      // Refetch data
      await fetchBillingData();
      return true;
    } catch (err) {
      console.error('Error updating billing record:', err);
      return false;
    }
  }, [month, fetchBillingData]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  return { data, summary, loading, error, refetch: fetchBillingData, updateBillingRecord };
}
